import { Building, ClockIcon, CrossIcon, PhoneIcon } from 'assets/icons';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import useText from 'hooks/useText';
import { attemptTag, sourceKey } from 'pages/prospection/callListItems';

const TAG_TONES = {
  discovery: 'bg-yellow-50 text-yellow-700',
  followup: 'bg-yellow-500 text-white',
};

const ACTION_CLASS =
  'flex size-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white';

// « {source} · {city} », with « - » for what is missing.
export const sublineOf = (text, company) => {
  const key = company && sourceKey(company.source);
  return text('row.subline', {
    source: key ? text(key) : '-',
    city: (company && company.city) || '-',
  });
};

/**
 * One call left to make, 64 px high: its rank (`rank`) or a clock for a
 * planned one, ringed in orange when `selected`; the company, the attempt tag,
 * then the phone button, or the remove cross while `editing`. The row itself
 * takes no click.
 */
const CallRow = ({
  line,
  rank,
  now,
  editing,
  selected,
  removeDisabled,
  onCall,
  onRemove,
}) => {
  const { text } = useText('prospection');
  const company = line.company || {};
  const tag = attemptTag(line, now);
  const label = text(tag.label.key, tag.label.values);
  const tagText = tag.suffix
    ? text(tag.suffix.key, { label, ...tag.suffix.values })
    : label;
  const callLabel = text('row.call', { phone: company.phone || '-' });
  const removeLabel = text(
    removeDisabled ? 'row.removeDisabled' : 'row.remove',
  );

  return (
    <div
      data-testid="call-row"
      data-selected={selected ? 'true' : 'false'}
      className={`flex h-16 items-center gap-3 border-b border-gray-100 px-4 ${selected ? 'bg-[#fcfcfc]' : 'bg-white'}`}
    >
      <span
        data-testid="call-row-rank"
        className={`flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium text-gray-700 ${selected ? 'border-yellow-500' : 'border-transparent'}`}
      >
        {rank ? rank : <ClockIcon wh={16} color="currentColor" />}
      </span>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-gray-100 text-gray-600">
        <Building wh={16} color="currentColor" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium leading-5 text-gray-900">
          {company.name || '-'}
        </span>
        <span className="truncate text-xs leading-4 text-gray-500">
          {sublineOf(text, company)}
        </span>
      </span>
      <span
        className={`shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${TAG_TONES[tag.tone] || TAG_TONES.discovery}`}
      >
        {tagText}
      </span>
      {editing ? (
        <IconTooltip label={removeLabel}>
          <button
            type="button"
            aria-label={text('row.remove')}
            disabled={removeDisabled}
            onClick={() => onRemove(line)}
            className={ACTION_CLASS}
          >
            <CrossIcon wh={18} color="currentColor" />
          </button>
        </IconTooltip>
      ) : (
        <IconTooltip label={callLabel}>
          <button
            type="button"
            aria-label={callLabel}
            onClick={() => onCall(line)}
            className={ACTION_CLASS}
          >
            <PhoneIcon wh={18} color="currentColor" />
          </button>
        </IconTooltip>
      )}
    </div>
  );
};

export default CallRow;
